import { useMutation } from '@apollo/client';
import { graphql } from '@generated/gql';
import { NetWorthWorkflowContext } from '@ovComponents/4-module/netWorthWorkflow/netWorthWorkflowContext';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
  useSnackbar,
} from 'hephaestus/web';
import { Refresh } from 'hephaestus/webIcons';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const RUN_WORKFLOW_SUB_STEP_ACTION = graphql(/** GraphQL */ `
  mutation runWorkflowSubStepAction($input: RunWorkflowSubStepActionInput!) {
    runWorkflowSubStepAction(input: $input) {
      workflowCompletion {
        id
      }
    }
  }
`);

const NetWorthAutomaticLink = ({
  onNext,
  workflowCompletion,
  subStepId,
}: {
  onNext: () => void;
  workflowCompletion: any;
  subStepId: string;
}) => {
  const { t } = useTranslation('netWorth');
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { netWorthData } = useContext(NetWorthWorkflowContext);
  const { workflowData } = useContext(WorkflowContext);

  const applicableData =
    Object.keys(netWorthData).length !== 0 ? netWorthData : workflowData;
  const notificationGroup =
    applicableData.netWorthData.netWorthLinkingNotificationGroup;

  const [runWorkflowSubStepAction] = useMutation(RUN_WORKFLOW_SUB_STEP_ACTION, {
    onCompleted: () => {
      let message = t(
        'addNetWorthAutomatically.netWorthAutomaticLink.syncMessage',
      );
      if (notificationGroup) {
        message = `${message} ${t('addNetWorthAutomatically.netWorthAutomaticLink.notificationMessage')}`;
      }
      enqueueSnackbar(message, { variant: 'success' });
      setError(null);
      onNext();
    },
  });

  const attemptIntegration = useCallback(async () => {
    if (!workflowCompletion.id) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const variables = {
      input: {
        workflowCompletionId: workflowCompletion.id,
        subStepId,
        actionType: applicableData.netWorthData.netWorthLinkingActionType,
      },
      skipErrorHandler: true,
    };

    try {
      await runWorkflowSubStepAction({ variables });
      return;
    } catch (err) {
      setError(err as Error);
    }
    setIsLoading(false);
  }, [workflowCompletion, subStepId, runWorkflowSubStepAction]);

  useEffect(() => {
    attemptIntegration();
  }, [attemptIntegration]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        minHeight: '300px',
      }}
    >
      <Typography variant='headingMedium'>
        {t('addNetWorthAutomatically.netWorthAutomaticLink.title')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '450px',
        }}
      >
        {error && (
          <Box
            sx={{
              mb: 2,
            }}
          >
            <Alert severity='error'>{error.message}</Alert>
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Button
                onClick={attemptIntegration}
                disabled={isLoading}
                variant='text'
                startIcon={<Refresh />}
              >
                {t('shared:retry')}
              </Button>
            </Box>
          </Box>
        )}
        {isLoading && <CircularProgress />}
      </Box>
    </Box>
  );
};

export default NetWorthAutomaticLink;
